import { colors, media } from "assets/style/Variable";
import { SvgPoint } from "assets/svg/common/CommonSvg";
import LineCheckbox from "components/element/LineCheckbox";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import styled from "styled-components";
import { MarkerPositionType } from "types/kakaoComon";
import { DEFAULT_ADDRESS_TEXT, isPcMo, weatherClock } from "utils/common";
import { getStableCurrentWeatherTimeLists } from "utils/weather/weather";
import { SearchWrap } from "./SearchWrap";
import { WeatherIcon } from "./weatherIcon/WeatherIcon";

interface WeatherHeaderType {
  addrUpdate: (searchCoords: MarkerPositionType) => void;
  useCurrentAddress: boolean;
  currentAddressChange: (checked: boolean) => void;
}

const AlertTriangleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
      stroke={colors.yellow}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 9v4" stroke={colors.yellow} strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1" fill={colors.yellow} />
  </svg>
);

export const WeatherHeader = ({ addrUpdate, useCurrentAddress, currentAddressChange }: WeatherHeaderType) => {
  const { data } = useSelector((state: RootState) => state.storeWeather);
  const useLocation = useSelector((state: RootState) => state.storeLocation);
  const addressText = useCurrentAddress && useLocation.address
    ? useLocation.address.address_name.split(" ").slice(0, 3).join(" ")
    : DEFAULT_ADDRESS_TEXT;
  const userDevices = isPcMo();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (!useCurrentAddress || !useLocation.coords) return;
    addrUpdate(useLocation.coords);
  }, [addrUpdate, useCurrentAddress, useLocation.coords]);

  const locationWeatherIcon = useMemo(() => {
    return getStableCurrentWeatherTimeLists(data?.res?.[0]?.timeLists ?? [], weatherClock());
  }, [data]);

  const latestUpdateText = useMemo(() => {
    const baseUpdate = `${data?.baseUpdate ?? ""}`;
    if (!/^\d{4}$/.test(baseUpdate)) return "업데이트 정보 없음";

    const hour = baseUpdate.slice(0, 2);
    const minute = baseUpdate.slice(2, 4);
    return `최신 업데이트 ${hour}:${minute}`;
  }, [data?.baseUpdate]);

  return (
    <StyleWeatherHeader>
      <div className="search-box">
        <SearchWrap searchUpdate={addrUpdate} />
      </div>
      <div className="location-area">
        <LineCheckbox
          id="weather-current-address"
          label="현 주소 보기"
          checked={useCurrentAddress}
          onChange={currentAddressChange}
          className="location-toggle"
        />
        <button
          type="button"
          className="location"
          title="현재 위치 날씨 보기"
          onClick={handleClick}
          disabled={useCurrentAddress && !useLocation.address}>
          {
            (useCurrentAddress && useLocation.address)
              ? <>
                  <span className="icon"><SvgPoint $fillColor={colors.mSlateBlue} /></span>
                  <span className="txt">{addressText}</span>
                  <span className="icon weather-icon">
                    {locationWeatherIcon && <WeatherIcon isAnimation={false} categoryLists={locationWeatherIcon.categoryList} />}
                  </span>
                </>
              : <>
                  <span className="icon"><SvgPoint $fillColor={colors.mSlateBlue} /></span>
                  <span className="txt">{addressText}</span>
                  <span className="icon weather-icon">
                    {locationWeatherIcon && <WeatherIcon isAnimation={false} categoryLists={locationWeatherIcon.categoryList} />}
                  </span>
                </>
          }
        </button>
        <p className="update-time">{latestUpdateText}</p>
      </div>
      {
        userDevices.devices === "pc" && (
          // PC는 접속 위치 오차 안내만 버튼+툴팁으로 노출
          <div className="guide-wrap">
            <button
              type="button"
              className="guide-btn"
              onClick={() => setTooltipOpen((prev) => !prev)}
              aria-expanded={tooltipOpen}>
              <span className="guide-icon"><AlertTriangleIcon /></span>
              <span className="guide-text">접속 위치 안내</span>
            </button>
            {
              tooltipOpen && (
                <div className="guide-tooltip">
                  PC에서는 접속 위치가 정확하지 않을 수 있어요. 현재 위치 버튼이나 검색으로 지역을 다시 선택해 주세요.
                </div>
              )
            }
          </div>
        )
      }
    </StyleWeatherHeader>
  );
};

const StyleWeatherHeader = styled.div`
  position:relative;
  z-index:20;
  display:flex;
  gap:20px;
  align-items:center;
  margin-top:30px;
  padding:10px 20px;
  border-radius:5px;
  ${({theme}) => theme.translucence};
  background: ${({theme}) => theme.opacityBg};
  .search-box {
    min-width:0;
  }
  .location-area {
    display:flex;
    align-items:center;
    gap:12px;
    flex:0 1 auto;
    min-width:0;
  }
  .location{
    display:flex;
    align-items:center;
    gap:6px;
    flex:0 1 auto;
    justify-content:flex-start;
    min-width:0;
    max-width:340px;
    .icon {
      flex-shrink:0;
      width:18px;
      height:18px;
    }
    .weather-icon {
      width:22px;
      height:22px;
      .error-text{
        font-size:12px;
        text-wrap:nowrap;
      }
    }
    .txt {
      font-size:14px;
      white-space: nowrap;
    }
    &:disabled {
      opacity:.6;
      cursor:default;
    }
  }
  .location-toggle {
    flex-shrink:0;
  }
  .update-time {
    flex-shrink:0;
    font-size:12px;
    color:${colors.subTextColor};
    white-space:nowrap;
  }
  .guide-wrap {
    position:relative;
    margin-left:auto;
    z-index:10;
  }
  .guide-btn {
    display:flex;
    align-items:center;
    gap:6px;
    color:${colors.yellow};
  }
  .guide-icon {
    width:16px;
    height:16px;
  }
  .guide-text {
    font-size:12px;
    white-space:nowrap;
  }
  .guide-tooltip {
    position:absolute;
    top:calc(100% + 8px);
    right:0;
    z-index:20;
    width:260px;
    padding:10px 12px;
    border-radius:8px;
    background:${({theme}) => theme.type === 'dark' ? 'rgba(16,16,16,.92)' : 'rgba(255,255,255,.96)'};
    box-shadow:0 8px 24px rgba(0,0,0,.12);
    font-size:12px;
    line-height:1.5;
  }
  ${media.mo}{
    flex-direction:column;
    gap:10px;
    align-items:stretch;
    margin-top:20px;
    .location-area {
      display:grid;
      grid-template-columns: 1fr;
      gap:6px;
      align-items:flex-start;
    }
    .location-toggle {
      width:fit-content;
    }
    .location {
      max-width:none;
      width:100%;
    }
    .update-time {
      font-size:11px;
    }
    .guide-wrap {
      display:none;
    }
  }
`;
